<div
  #saveScheduleModal
  tabindex="-1"
  class="fixed top-0 left-0 right-0 z-50 hidden w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full"
  role="dialog"
  aria-modal="true"
  aria-labelledby="save-schedule-title"
  (keydown.escape)="closeSaveScheduleModal()"
>
  <div
    class="relative w-full max-h-full"
    [class.max-w-md]="step() !== ScheduleSaveStep.SUMMARY"
    [class.max-w-xl]="step() === ScheduleSaveStep.SUMMARY"
    cdkTrapFocus
    [cdkTrapFocusAutoCapture]="saveScheduleShowed()"
  >
    <div
      class="relative flex flex-col gap-6 bg-primary-purple-light-2 rounded-lg shadow p-6"
      [class]="step() === ScheduleSaveStep.SUMMARY ? 'h-[42rem] max-h-[calc(100vh-2rem)]' : ''"
    >
      <header class="flex shrink-0 items-center justify-between gap-4">
        <h2 id="save-schedule-title" class="text-base font-bold text-primary-purple">{{ literalService.get(title(), true) }}</h2>
        <div class="flex items-center gap-2 shrink-0">
          @if (step() === ScheduleSaveStep.SUMMARY) {
            <app-custom-toggle
              [id]="'save-schedule-view-toggle'"
              [selectedValue]="summaryView()"
              [options]="summaryViewToggleOptions"
              (action)="toggleSummaryView()"
            />
          }
          @if (step() !== ScheduleSaveStep.SAVING) {
            <button
              type="button"
              class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-transparent hover:bg-neutrals-grey-01 cursor-pointer focus:outline-none focus-visible:outline-2 focus-visible:outline-primary-purple"
              [attr.aria-label]="literalService.get('close', true)"
              (click)="closeSaveScheduleModal()"
            >
              <img [src]="images.close" alt="" aria-hidden="true" class="h-6 w-6" />
            </button>
          }
        </div>
      </header>

      @switch (step()) {
        @case (ScheduleSaveStep.CONFIRM) {
          <p class="text-sm">{{ literalService.get('vehicles.actions.saveSchedule.description', true, { count: changes().length }) }}</p>
          <div class="flex items-center justify-between">
            <div class="flex gap-2 items-center min-w-0 mr-2">
              <img [src]="images.booking.notifications" alt="" aria-hidden="true" class="h-6 w-6 shrink-0" />
              <label for="save-schedule-notify" class="font-bold cursor-pointer">
                {{ literalService.get('vehicles.actions.saveSchedule.sendNotification', true) }}
              </label>
            </div>
            <app-toggle
              class="shrink-0"
              [id]="'save-schedule-notify'"
              [checked]="sendNotification()"
              (change)="sendNotification.set(!sendNotification())"
            />
          </div>
          <div class="flex gap-2">
            <button type="button" class="secondary p-2 border-2 font-semibold h-10 w-full uppercase cursor-pointer" (click)="closeSaveScheduleModal()">
              {{ literalService.get('cancel', true) }}
            </button>
            <button type="button" class="primary p-2 border-2 font-semibold h-10 w-full uppercase cursor-pointer" (click)="saveSchedule()">
              {{ literalService.get('confirm', true) }}
            </button>
          </div>
        }
        @case (ScheduleSaveStep.SAVING) {
          <div class="flex flex-col items-center gap-4" aria-busy="true">
            <div class="w-64 motion-reduce:hidden" aria-hidden="true">
              <ng-lottie height="100%" width="100%" [options]="loadingAnimation" (animationCreated)="utilsService.animationCreated($event, 1)" />
            </div>
            <div class="flex w-full justify-between items-center">
              <p class="text-lg font-bold">{{ literalService.get('doingAction.title', true) }}</p>
              <span class="text-lg font-semibold text-primary-purple-light">{{ progress() }}%</span>
            </div>
            <progress
              class="progress text-primary-purple-light w-full"
              [value]="progress()"
              max="100"
              [attr.aria-label]="literalService.get('vehicles.actions.saveSchedule.progress', true)"
            ></progress>
            <p class="w-full text-center text-base truncate" aria-live="polite">{{ progressDetail() }}</p>
          </div>
        }
        @case (ScheduleSaveStep.SUMMARY) {
          @if (failedView() && operationError(); as error) {
            <p class="flex shrink-0 gap-2 items-start text-secondary-red font-semibold" role="alert">
              <img [src]="images.error" alt="" aria-hidden="true" class="h-5 w-5 shrink-0" />
              {{ error }}
            </p>
          }
          <div class="tabs tabs-border shrink-0" role="tablist" [attr.aria-label]="literalService.get(title(), true)">
            @for (tab of summaryTabs; track tab) {
              <button
                type="button"
                role="tab"
                [id]="'save-schedule-tab-' + tab"
                aria-controls="save-schedule-panel"
                [attr.aria-selected]="summaryTab() === tab"
                class="tab gap-2 text-sm font-semibold focus:outline-none focus-visible:outline-2 focus-visible:outline-primary-purple"
                [class.tab-active]="summaryTab() === tab"
                [class.text-primary-purple-light]="summaryTab() === tab"
                [class.text-neutrals-grey-04]="summaryTab() !== tab"
                (click)="summaryTab.set(tab)"
              >
                {{ literalService.get(summaryTabLabelKeys[tab], true) }}
                <span
                  class="rounded-full px-1.5 text-xs font-semibold"
                  [class]="failedView() ? 'bg-secondary-red/10 text-secondary-red' : 'bg-secondary-green/10 text-secondary-green-dark'"
                >{{ tabCounts()[tab] }}</span>
              </button>
            }
          </div>
          <div class="flex min-h-0 flex-1 flex-col gap-6">
            <div
              id="save-schedule-panel"
              role="tabpanel"
              tabindex="0"
              [attr.aria-labelledby]="'save-schedule-tab-' + summaryTab()"
              class="min-h-0 overflow-y-auto rounded-lg border border-neutrals-grey-02 bg-neutrals-white focus:outline-none focus-visible:outline-2 focus-visible:outline-primary-purple"
            >
              @if (visibleRows().length) {
                <table class="w-full text-sm">
                  <caption class="sr-only">{{ literalService.get(summaryTabLabelKeys[summaryTab()], true) }}</caption>
                  <thead class="sr-only">
                    <tr>
                      <th scope="col">{{ literalService.get(summaryTabLabelKeys[summaryTab()], true) }}</th>
                      <th scope="col">
                        @if (failedView()) {
                          {{ literalService.get('vehicles.actions.saveSchedule.reason', true) }}
                        } @else {
                          {{ literalService.get('vehicles.actions.saveSchedule.newValues', true) }}
                        }
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of visibleRows(); track row.key) {
                      <tr class="border-b border-neutrals-grey-02 last:border-b-0">
                        <td class="w-2/5 border-r border-neutrals-grey-02 p-3 align-middle">
                          <div class="flex items-center gap-2">
                            @if (row.service) {
                              <span class="shrink-0" aria-hidden="true" [tooltip]="row.service.name || row.serviceLabel || ''">
                                <app-service-tag [serviceInfo]="row.service" [compact]="true" />
                              </span>
                              <span class="sr-only">{{ row.serviceLabel }}</span>
                            } @else {
                              <img [src]="images.route" alt="" aria-hidden="true" class="h-5 w-5 shrink-0 filter-primary-purple-light" />
                            }
                            <div class="flex min-w-0 flex-col">
                              <span class="font-semibold truncate">{{ row.title }}</span>
                              @if (failedView() && row.timeRange) {
                                <span class="text-xs text-neutrals-grey-05">{{ row.timeRange }}</span>
                              }
                            </div>
                          </div>
                        </td>
                        <td class="p-3 align-middle">
                          @if (failedView()) {
                            <p class="flex items-center gap-2" [class.text-secondary-red]="row.causedFailure" [class.font-semibold]="row.causedFailure">
                              <img [src]="row.causedFailure ? images.cancelCircle : images.notifications.info" alt="" aria-hidden="true" class="h-5 w-5 shrink-0" [class.filter-secondary-red]="row.causedFailure" />
                              {{ row.reason }}
                            </p>
                          } @else {
                            <ul class="flex flex-col gap-1">
                              <li class="flex items-center gap-2">
                                <img [src]="images.setup.card.time" alt="" aria-hidden="true" class="h-4 w-4 shrink-0" />
                                {{ row.timeRange }}
                              </li>
                              @if (row.driver) {
                                <li class="flex items-center gap-2">
                                  <img [src]="images.driver" alt="" aria-hidden="true" class="h-4 w-4 shrink-0" />
                                  {{ row.driver }}
                                </li>
                              }
                              @if (row.vehicle) {
                                <li class="flex items-center gap-2">
                                  <img [src]="images.vehicle" alt="" aria-hidden="true" class="h-4 w-4 shrink-0" />
                                  {{ row.vehicle }}
                                </li>
                              }
                            </ul>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              } @else {
                <p class="p-4 text-sm text-neutrals-grey-04">
                  @if (failedView()) {
                    {{ literalService.get('vehicles.actions.saveSchedule.emptyFailed', true) }}
                  } @else {
                    {{ literalService.get('vehicles.actions.saveSchedule.emptySucceeded', true) }}
                  }
                </p>
              }
            </div>
            <div class="flex shrink-0 justify-end">
              <button type="button" class="primary px-8 border-2 font-semibold h-10 uppercase cursor-pointer" (click)="closeSaveScheduleModal()">
                {{ literalService.get('continue', true) }}
              </button>
            </div>
          </div>
        }
      }
    </div>
  </div>
</div>
